/**
 * Provider-editable frontmatter fields for an entity card adjustment.
 *
 * When a provider clicks "Adjust" on a proposed ingestion change, or
 * "Adjust card details" on a settled card, this component presents the
 * clinician-facing editable fields: Name, Type (kind), and Importance
 * (salience). Read-only fields (id, patient_id, status, links, sources,
 * events, attributes, aliases) are not shown — the provider only adjusts
 * the fields that carry structural or clinical judgment. The component is
 * intentionally simple so non-tech-savvy clinicians can use it without
 * instruction.
 *
 * Type is a combobox: the 14 known kinds are listed as friendly labels
 * (clinical kinds first), with an "Other…" free-text entry for open
 * taxonomy extensions. The Type field is hidden for anchor cards
 * (kind = "section" | "overview") whose identity is immutable.
 *
 * Importance (salience) and Type are select / combo controls; Name is a
 * free-text input. All other EntityFrontmatter keys are preserved verbatim
 * in the submitted payload.
 */
import React from "react";
import { CLINICAL_KINDS, humanizeKind } from "../blueprint/cardRanking";
import type { EntityFrontmatter } from "../../types";

/** Human-readable label for a salience value. */
const SALIENCE_LABELS: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

/** The remaining known kinds, in alphabetical order. */
const OTHER_KINDS: readonly string[] = [
  "activity",
  "event",
  "group",
  "interest",
  "person",
  "place",
  "protective_factor",
  "stressor",
  "strength",
  "theme",
];

/** Fast membership test — all 14 taxonomy kinds. */
const ALL_KNOWN_KINDS: ReadonlySet<string> = new Set([
  ...CLINICAL_KINDS,
  ...OTHER_KINDS,
]);

/** The sentinel value used inside the <select> to signal "free-form entry". */
const OTHER_VALUE = "__other__";

/** Props for {@link FrontmatterEditor}. */
export interface FrontmatterEditorProps {
  /**
   * The proposed frontmatter to pre-populate. Required keys (`id`,
   * `patient_id`, `kind`, `name`) must be present — the editor preserves them
   * in the submitted value so the backend `parse_frontmatter` can validate.
   */
  value: EntityFrontmatter;
  /** Called with the updated frontmatter whenever the provider edits a field. */
  onChange: (updated: EntityFrontmatter) => void;
  /**
   * When `true` the card is a Blueprint anchor card (kind = "section" |
   * "overview") whose `kind` is immutable — the Type field is hidden.
   */
  isAnchor?: boolean;
}

/**
 * Editable controls for the provider-adjustable entity frontmatter fields.
 *
 * @param props - The proposed frontmatter, a change callback, and an optional
 *   anchor flag.
 * @returns The editor element.
 */
export const FrontmatterEditor: React.FC<FrontmatterEditorProps> = ({
  value,
  onChange,
  isAnchor = false,
}) => {
  const handleName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onChange({ ...value, name: e.target.value });
  };

  const handleSalience = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    onChange({ ...value, salience: e.target.value });
  };

  const handleKindSelect = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const v = e.target.value;
    if (v === OTHER_VALUE) {
      // Clear kind so the text input starts blank for a free-form entry.
      onChange({ ...value, kind: "" });
    } else {
      onChange({ ...value, kind: v });
    }
  };

  const handleKindText = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onChange({ ...value, kind: e.target.value });
  };

  const kindIsKnown = ALL_KNOWN_KINDS.has(value.kind);
  const selectValue = kindIsKnown ? value.kind : OTHER_VALUE;
  const showCustomInput = !kindIsKnown;

  return (
    <div
      className="space-y-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3"
      data-testid="frontmatter-editor"
    >
      <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
        Adjust card details
      </div>

      {/* Name */}
      <label className="block space-y-1">
        <span className="text-xs font-medium text-slate-700">Name</span>
        <input
          type="text"
          value={value.name}
          onChange={handleName}
          className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none"
          data-testid="frontmatter-name"
        />
      </label>

      {/* Type (kind) — hidden for anchor cards */}
      {!isAnchor && (
        <label className="block space-y-1">
          <span className="text-xs font-medium text-slate-700">Type</span>
          <select
            value={selectValue}
            onChange={handleKindSelect}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none"
            data-testid="frontmatter-kind"
          >
            <optgroup label="Clinical">
              {CLINICAL_KINDS.map((k) => (
                <option key={k} value={k}>
                  {humanizeKind(k)}
                </option>
              ))}
            </optgroup>
            <optgroup label="Other">
              {OTHER_KINDS.map((k) => (
                <option key={k} value={k}>
                  {humanizeKind(k)}
                </option>
              ))}
            </optgroup>
            <option value={OTHER_VALUE}>Other…</option>
          </select>
          {showCustomInput && (
            <input
              type="text"
              value={value.kind}
              onChange={handleKindText}
              placeholder="Enter a custom type"
              className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none"
              data-testid="frontmatter-kind-custom"
            />
          )}
        </label>
      )}

      {/* Importance (salience) */}
      <label className="block space-y-1">
        <span className="text-xs font-medium text-slate-700">Importance</span>
        <select
          value={value.salience}
          onChange={handleSalience}
          className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none"
          data-testid="frontmatter-salience"
        >
          {Object.entries(SALIENCE_LABELS).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
};

export default FrontmatterEditor;
